/** STBH khớp 10M / 15M / 20M → mức hàng hóa */
const FIRE_EXPLOSION_HANG_HOA_BY_STBH = new Map([
  [10_000_000, 10_000_000],
  [15_000_000, 15_000_000],
  [20_000_000, 20_000_000],
]);

/** Hàng hóa theo hạng gói STBH thấp → cao */
const FIRE_EXPLOSION_HANG_HOA_BY_RANK = [
  10_000_000, 15_000_000, 20_000_000,
];

/** Màu accent, màu nền icon và tagline theo rank (index 0 = thấp nhất → tăng dần) */
const TIER_META = [
  {
    tierColor: "#EA580C",
    tierBgColor: "#FFEDD5",
    tierTagline: "Phù hợp cửa hàng nhỏ",
    tierTaglineColor: "#C05621",
  },
  {
    tierColor: "#64748B",
    tierBgColor: "#F1F5F9",
    tierTagline: "Được chọn nhiều nhất",
    tierTaglineColor: "#64748B",
  },
  {
    tierColor: "#EAB308",
    tierBgColor: "#FEF3C7",
    tierTagline: "Bảo vệ toàn diện nhất",
    tierTaglineColor: "#B45309",
  },
];

export const formatVnd = (amount) =>
  `${Number(amount).toLocaleString("vi-VN")}đ`;

export const formatMoneyVN = (value) => {
  if (!value) return "0";

  if (value >= 1_000_000_000) {
    const billion = value / 1_000_000_000;
    const formatted = parseFloat(billion.toFixed(2)).toString();
    return `${formatted} tỉ`;
  }

  if (value >= 1_000_000) {
    const million = Math.floor(value / 1_000_000);
    return `${million}tr`;
  }

  return value.toLocaleString("vi-VN");
};

export const getTierLabel = (product) => {
  const name = String(product?.name || "").trim();
  if (!name) return "Gói";
  const parts = name.split("-");
  if (parts.length > 1) return parts[parts.length - 1].trim().toUpperCase();
  return name.toUpperCase();
};

export const getProductPremium = (product) => {
  if (Number.isFinite(+product?.totalAllPremium) && +product.totalAllPremium > 0) {
    return +product.totalAllPremium;
  }
  const firstRate = product?.benefits?.[0]?.rate?.[0];
  const premiumValue = firstRate?.premiumRule?.premium_value;
  if (Number.isFinite(+premiumValue) && +premiumValue > 0) {
    return +premiumValue;
  }
  return 0;
};

/** Khóa ổn định để chọn đúng biến thể (công ty) khi thanh toán */
export function getFireExplosionVariantKey(variant, fallbackIndex = 0) {
  const key = variant?.productId ?? variant?.id;
  if (key != null && key !== "") return String(key);
  return `fe-var-${fallbackIndex}`;
}

export function normalizeFireExplosionPackages(insuranceFireExplosionProduct) {
  if (!Array.isArray(insuranceFireExplosionProduct)) return [];
  const rows = insuranceFireExplosionProduct.map((product, index) => {
    const premium = getProductPremium(product);
    const originalPremium = Number(product?.originalPremium || 0);
    const totalAllSumAssured = Number(product?.totalAllSumAssured) || 0;
    return { product, index, premium, originalPremium, totalAllSumAssured };
  });
  /** Hạng theo mức STBH duy nhất (không theo từng dòng API) để nhiều công ty cùng gói vẫn đúng Cháy nổ / Hàng hóa */
  const uniqueStbhSorted = [...new Set(rows.map((r) => r.totalAllSumAssured))].sort(
    (a, b) => a - b
  );
  const subtractBySumAssured = {};
  uniqueStbhSorted.forEach((stbh, rank) => {
    const sub = FIRE_EXPLOSION_HANG_HOA_BY_RANK[rank];
    subtractBySumAssured[stbh] = sub !== undefined ? sub : null;
  });
  const flat = rows.map(
    ({ product, index, premium, originalPremium, totalAllSumAssured }) => {
      const byStbh = FIRE_EXPLOSION_HANG_HOA_BY_STBH.get(totalAllSumAssured);
      const byRank = subtractBySumAssured[totalAllSumAssured];
      const hangHoaSumAssured = byStbh !== undefined ? byStbh : byRank;
      const chayNoSumAssured =
        hangHoaSumAssured != null
          ? Math.max(0, totalAllSumAssured - hangHoaSumAssured)
          : null;
      return {
        id: String(product?.id || product?.code || product?.productCode || index),
        code: product?.code || product?.productCode || "",
        label: getTierLabel(product),
        durationLabel: product?.durationLabel || "1 Năm",
        premium,
        totalAllSumAssured,
        originalPremium,
        ...product,
        hangHoaSumAssured,
        chayNoSumAssured,
      };
    }
  );

  const groupMap = new Map();
  for (const pkg of flat) {
    const groupKey = `${pkg.totalAllSumAssured}|${pkg.label}`;
    if (!groupMap.has(groupKey)) {
      const tierId = `fe-tier-${groupKey.replace(/[^a-zA-Z0-9]+/g, "-")}`;
      groupMap.set(groupKey, {
        id: tierId,
        label: pkg.label,
        durationLabel: pkg.durationLabel,
        totalAllSumAssured: pkg.totalAllSumAssured,
        hangHoaSumAssured: pkg.hangHoaSumAssured,
        chayNoSumAssured: pkg.chayNoSumAssured,
        variants: [],
      });
    }
    groupMap.get(groupKey).variants.push(pkg);
  }

  const groups = Array.from(groupMap.values()).map((g) => ({
    ...g,
    variants: [...g.variants].sort((a, b) => {
      const dp = a.premium - b.premium;
      if (dp !== 0) return dp;
      return getFireExplosionVariantKey(a).localeCompare(
        getFireExplosionVariantKey(b)
      );
    }),
  }));

  groups.sort((a, b) => a.totalAllSumAssured - b.totalAllSumAssured);

  // Gán tierColor + tierBgColor + tierTagline theo rank (index sau khi sort)
  groups.forEach((g, i) => {
    const meta = TIER_META[i] || TIER_META[TIER_META.length - 1];
    g.tierColor = meta.tierColor;
    g.tierBgColor = meta.tierBgColor;
    g.tierTagline = meta.tierTagline;
    g.tierTaglineColor = meta.tierTaglineColor;
  });

  return groups;
}
